<script setup lang="ts">
import { ref, useTemplateRef } from "vue";
import { computed } from "vue";

import { useEditSiteConsumptionInfosMutation } from "@/composables/kili/consumption/mutations/use-edit-site-consumption-infos-mutation";
import { useGetSiteConsumptionInfosQuery } from "@/composables/kili/consumption/queries/use-get-site-consumption-infos-query";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { EDataUnit } from "@/constants/data-units.type";
import {
  EElectricityTarif,
  TEditSiteConsumptionInfosBody,
} from "@/data/api/kili/consumption/types/consumption-infos.type";
import { TSiteDetails } from "@/data/api/kili/sites/types/sites.type";
import { V4HSiteId } from "@/data/common/ids";
import { useTypedIntl } from "@/locales/typed-intl";
import { isDefined } from "@/utils/object";

import EditSiteDetailsDrawer from "@/components/tools/site-details/edit-site-details/edit-site-details-drawer.vue";

interface IProps {
  siteId: V4HSiteId;
  siteDetails?: TSiteDetails;
  isLoading?: boolean;
}

const props = defineProps<IProps>();

const { t, k, n } = useTypedIntl();

const _siteId = computed<number>(() => props.siteId);

const { data: siteConsumptionInfos, isPending: isSiteConsumptionInfosPending } =
  useGetSiteConsumptionInfosQuery(_siteId);

const { mutateAsync: editSiteConsumptionInfos, isPending: isEditSiteConsumptionInfosPending } =
  useEditSiteConsumptionInfosMutation();

const isEditSiteDetailsDrawerVisible = ref<boolean>(false);

const editSiteDetailsDrawer = useTemplateRef("editSiteDetailsDrawer");

function getTarifValue(tarif: EElectricityTarif): string {
  const tarifValue = siteConsumptionInfos.value?.electricityPrices[tarif];
  return isDefined(tarifValue) ? n(tarifValue, { maximumFractionDigits: 4 }) : "--";
}

async function handleEditSiteConsumptionInfos(infos: TEditSiteConsumptionInfosBody) {
  await editSiteConsumptionInfos({
    siteId: _siteId,
    body: infos,
  }).then(() => editSiteDetailsDrawer.value?.closeDrawer());
}
</script>

<template>
  <div
    class="bg-white/50 rounded-[18px] p-[18px] flex flex-row justify-between overflow-clip relative"
    :data-test-id="DATA_TEST_IDS.components.siteDetailsCard.component"
  >
    <div
      v-if="isSiteConsumptionInfosPending"
      class="absolute bg-white/50 inset-0"
      :data-test-id="DATA_TEST_IDS.components.siteDetailsCard.skeleton"
    />
    <div class="flex flex-row gap-2">
      <div class="bg-white rounded-lg border w-[44px] h-[44px] flex justify-center items-center">
        <i class="pi pi-building" />
      </div>

      <div class="flex flex-col">
        <h5
          class="font-semibold"
          :data-test-id="DATA_TEST_IDS.components.siteDetailsCard.siteName"
        >
          {{ siteDetails?.name }}
        </h5>
        <span
          class="font-normal text-text-secondary"
          :data-test-id="DATA_TEST_IDS.components.siteDetailsCard.siteArea"
        >
          {{
            t(
              k.global.units[EDataUnit.SQUARE_METER]({
                value: isDefined(siteConsumptionInfos?.siteArea) ? siteConsumptionInfos?.siteArea : "--",
              }),
            )
          }}
        </span>
      </div>
    </div>

    <div class="flex flex-row gap-8 items-center">
      <div
        v-for="tarif in Object.values(EElectricityTarif)"
        :key="tarif"
        class="flex flex-col"
        :data-test-id="DATA_TEST_IDS.components.siteDetailsCard.electricityTarifs.item"
      >
        <div class="flex flex-row items-center">
          <span
            class="font-semibold"
            :data-test-id="DATA_TEST_IDS.components.siteDetailsCard.electricityTarifs.tarifValue"
          >
            {{ t(k.global.units[EDataUnit.EURO]({ value: getTarifValue(tarif) })) }}
          </span>
          <small class="text-text-secondary">/kwh</small>
        </div>
        <small
          class="text-text-secondary"
          :data-test-id="DATA_TEST_IDS.components.siteDetailsCard.electricityTarifs.tarifType"
        >
          {{ t(k.global.fullTarifType[tarif]) }}</small
        >
      </div>

      <hr class="border-r flex h-[22px]" />

      <button
        class="bg-white rounded-lg border w-[34px] h-[34px] flex justify-center items-center"
        :data-test-id="DATA_TEST_IDS.components.siteDetailsCard.editSiteDetailsButton"
        @click="isEditSiteDetailsDrawerVisible = true"
      >
        <i class="pi pi-pencil text-brand" />
      </button>
    </div>
  </div>

  <EditSiteDetailsDrawer
    ref="editSiteDetailsDrawer"
    v-model:visible="isEditSiteDetailsDrawerVisible"
    :site-consumption-infos="siteConsumptionInfos"
    :is-loading="isEditSiteConsumptionInfosPending"
    :site-name="props.siteDetails?.name"
    @submit="handleEditSiteConsumptionInfos($event)"
  />
</template>
